import {
  findCitationSentences,
  findTermSentences,
  sentenceLabel,
  type Sentence,
} from "./evidence.ts";
import type {
  Candidate,
  CaseTextDocument,
  CaseTextStatus,
  Dossier,
  Evidence,
  EvidenceSentence,
  Pair,
  SearchProvider,
  SearchResult,
} from "./types";

/**
 * 로컬 볼륨(statute-core + judgment-case-full + case-bm25) 기반 SearchProvider.
 *
 * DB·wasm 커널·BM25 리더는 워커가 소유하고, 이 클래스는 **작은 transport**에만
 * 의존한다. 그래서 Node 테스트에서는 node:sqlite + fs 기반 transport를 주입해
 * 같은 SQL/같은 dossier 조립 로직을 그대로 검증할 수 있다.
 *
 * 정본 흐름:
 *   조문 쿼리    : kernel.search(q, 200, limit)
 *   조문→direct  : resolve SQL → article_locus → tail 역인덱스
 *   조문→related : BM25C1(article text, direct 제외) — 이웃 조문 휴리스틱 아님
 *   자유 텍스트  : 조문 검색 + BM25 판례 키워드 검색(panel "case")
 *   사건번호     : akn_cases point lookup(panel "case", 1건)
 *
 * KURE는 여기서 호출하지 않는다(useDossier가 buildPasses로 따로 돌린다).
 */

export type CaseProvenance = "ref" | "body";
export type { CaseTextStatus } from "./types";

export interface LocalStatuteHit {
  rank: number;
  pointer: string;
  label: string;
  /** akn_nodes.plain_text (있으면) */
  text?: string;
  /** akn_nodes.hierarchy_path (조문 breadcrumb) */
  hierarchy?: string;
  exact?: boolean;
  semantic?: number;
  lexical?: number;
}

export interface LocalStatuteSearch {
  query: string;
  matched?: string[];
  hits: LocalStatuteHit[];
}

export interface LocalCaseRef {
  pointer: string;
  court?: string;
  caseNo?: string;
  decisionDate?: string;
  provenance?: CaseProvenance;
}

/** BM25 한 건. score는 raw BM25이며 0–1 정규화하지 않는다. */
export interface LocalCaseHit {
  pointer: string;
  score: number;
  matchedTerms: string[];
  court?: string;
  caseNo?: string;
  decisionDate?: string;
  /** include_direct=true일 때만 의미가 있다. */
  alsoCited?: boolean;
}

export interface LocalCaseSearch {
  query: string;
  terms?: number;
  matched?: number;
  hits: LocalCaseHit[];
}

export interface LocalRelatedCases {
  status: "ok" | "empty" | "unavailable";
  hits: LocalCaseHit[];
  /** direct 인용 전체 건수(제외 전). */
  directTotal?: number;
  /** 후보에서 실제로 제외한 direct 건수. */
  excludedDirect?: number;
  candidatePool?: number;
}

export interface LocalDirectCases {
  status: "ok" | "not_in_core";
  law?: string;
  article?: string;
  pointer?: string;
  label?: string;
  /** akn_nodes.plain_text */
  text?: string;
  /** akn_nodes.heading (조문 제목, BM25 related 질의에 쓴다) */
  heading?: string;
  /** akn_nodes.hierarchy_path */
  hierarchy?: string;
  locus?: string;
  /** tail 역인덱스 전체 건수(ref + body). cases는 앞쪽 일부만 담는다. */
  total?: number;
  /** direct 전체 pointer(중복 없음). related에서 제외할 때 쓴다. */
  pointers?: string[];
  cases: LocalCaseRef[];
}

export interface LocalCaseText extends CaseTextDocument {}

/** 워커(또는 Node 테스트)가 구현하는 최소 계약. */
export interface LocalTransport {
  searchStatutes(query: string, limit: number): Promise<LocalStatuteSearch>;
  directCases(law: string, article: string): Promise<LocalDirectCases>;
  /** 판례 전문 한 건. 없으면 전문 볼륨 미지원으로 본다. */
  caseText?(pointer: string): Promise<LocalCaseText>;
  /** 전문 볼륨이 열려 있는지. 있으면 결과 행마다 조회하지 않는다. */
  caseTextReady?(): Promise<boolean>;
  /**
   * BM25 판례 키워드 검색. 단일 토큰 질의(`부당해고`)를 허용하므로
   * matched >= 1이다(조문/related는 >= 2).
   */
  searchCases?(query: string, limit: number): Promise<LocalCaseSearch>;
  /**
   * 조문 본문 → 관련 판례(BM25). `excludePointers`는 direct 인용 집합이다.
   * `includeDirect`는 디버그/테스트용(alsoCited 확인).
   */
  relatedCases?(
    locus: string,
    articleText: string,
    excludePointers: readonly string[],
    limit: number,
    includeDirect?: boolean,
  ): Promise<LocalRelatedCases>;
  /** 사건번호 정규화 조회. 없으면 null. */
  caseByNumber?(caseNo: string, court?: string): Promise<LocalCaseRef | null>;
}

export type LocalQueryRoute =
  | { kind: "case"; caseNo: string; court?: string; raw: string }
  | { kind: "direct"; law: string; article: string; raw: string }
  | { kind: "needs_law"; article: string; raw: string }
  | { kind: "statute"; text: string };

export interface LocalProviderOptions {
  /** kernel.search candidates 폭 (제품 기본 200). */
  candidates?: number;
  /** 조문 검색 반환 수. */
  statuteLimit?: number;
  /** dossier에 담는 판례 수(랭커 비용 상한). */
  maxCases?: number;
  /** free text에서 관련 판례를 조회할 조문 수. */
  relatedStatutes?: number;
  /** pairs로 묶을 최대 개수. */
  maxPairs?: number;
  /** 조문→관련 판례(BM25) 결과에 담을 최대 판례 수(10–20). */
  relatedCaseLimit?: number;
  /** 자유 텍스트 판례 키워드 검색 결과 수. */
  caseSearchLimit?: number;
}

const DEFAULT_OPTIONS: Required<LocalProviderOptions> = {
  candidates: 200,
  statuteLimit: 10,
  maxCases: 8,
  relatedStatutes: 1,
  maxPairs: 4,
  relatedCaseLimit: 10,
  caseSearchLimit: 20,
};

export const MISSING_LAW_MESSAGE = "법령명이 필요합니다. 예: 민법 제750조";

const ARTICLE_RE = /(?:^|[\s,·])(?:제\s*)?(\d+)\s*조(?:\s*의\s*(\d+))?/;

/**
 * 사건번호. 공백을 모두 지운 뒤 매칭한다.
 *   2024다208865 / 대법원 2024다208865 / 대법원-2024다208865 / 87르62
 */
const CASE_NUMBER_RE = /^(?:([가-힣]{2,})-?)?(\d{2,4}[가-힣]{1,3}\d+)$/;

/** "민법 제750조" / "민법 750조" / "제750조" / 사건번호 / 자유문장을 라우팅한다. */
export function routeQuery(query: string): LocalQueryRoute {
  const raw = query.trim();
  const compact = raw.replace(/\s+/g, "");
  const caseMatch = CASE_NUMBER_RE.exec(compact);
  if (caseMatch) {
    return {
      kind: "case",
      caseNo: caseMatch[2]!,
      court: caseMatch[1],
      raw,
    };
  }

  const match = ARTICLE_RE.exec(raw);
  if (!match) return { kind: "statute", text: raw };

  const num = match[1]!;
  const sub = match[2];
  const article = `제${num}조${sub ? `의${sub}` : ""}`;
  const prefix = raw
    .slice(0, match.index)
    .replace(/[,·]+$/, "")
    .trim();
  const law = normalizeLawName(prefix);
  if (!law) return { kind: "needs_law", article, raw };
  return { kind: "direct", law, article, raw };
}

/** "민법상" → "민법". DB의 law_name은 짧은 정식 명칭이다. */
export function normalizeLawName(value: string): string {
  let law = value.replace(/\s+/g, " ").trim();
  if (law.length > 2 && law.endsWith("상")) law = law.slice(0, -1);
  return law;
}

function evidence(partial: Partial<Evidence>): Evidence {
  return {
    statutes: [],
    citedCases: [],
    directCites: [],
    ruleHits: [],
    ...partial,
  };
}

function caseLabel(ref: LocalCaseRef): string {
  return ref.caseNo ?? ref.pointer;
}

function caseText(ref: LocalCaseRef): string {
  return [ref.court, ref.decisionDate, ref.caseNo].filter(Boolean).join(" ") || ref.pointer;
}

/** "2024-08-13" → "2024. 8. 13." */
export function formatDecisionDate(iso?: string): string | undefined {
  if (!iso) return undefined;
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  if (!match) return iso;
  return `${match[1]}. ${Number(match[2])}. ${Number(match[3])}.`;
}

/** 복사용 인용: "대법원 2024. 8. 13. 선고 2024다208865 판결" */
export function formatCaseCitation(ref: LocalCaseRef): string {
  const head = [ref.court, formatDecisionDate(ref.decisionDate)].filter(Boolean).join(" ");
  const number = ref.caseNo ?? ref.pointer;
  return head ? `${head} 선고 ${number} 판결` : `${number} 판결`;
}

function statuteCandidate(hit: LocalStatuteHit): Candidate {
  return {
    id: hit.pointer,
    kind: "statute",
    label: hit.label,
    text: hit.text ?? hit.label,
    meta: hit.label,
  };
}

function caseCandidate(ref: LocalCaseRef): Candidate {
  return {
    id: ref.pointer,
    kind: "case",
    label: caseLabel(ref),
    text: caseText(ref),
    meta: [ref.court, ref.decisionDate].filter(Boolean).join(" ") || undefined,
  };
}

function scoreAt(rank: number): number {
  return 1 / (1 + Math.max(0, rank - 1) * 0.25);
}

type QuoteFields = Pick<SearchResult, "evidenceSentences" | "quote">;

/** 문장 목록을 카드/복사용 필드로 바꾼다. 첫 문장이 복사 기본값이다. */
function quoteFields(
  sentences: Sentence[],
  kind: EvidenceSentence["kind"],
): QuoteFields {
  const evidenceSentences: EvidenceSentence[] = sentences.map((sentence) => ({
    index: sentence.index,
    text: sentence.text,
    label: sentenceLabel(sentence),
    kind,
  }));
  const first = evidenceSentences[0];
  return first ? { evidenceSentences, quote: first.text } : {};
}

/** 너무 흔해서 근거 문장을 고르는 데 도움이 안 되는 말. */
const FALLBACK_STOPWORDS = new Set([
  "또는",
  "경우",
  "관련",
  "대한",
  "위한",
  "따라",
  "의한",
  "있는",
  "없는",
  "한다",
  "되었다",
  "그",
  "및",
  "제",
]);

/**
 * direct 판례가 **본문에서** 조문 번호를 다시 적지 않는 경우(참조 목록에만
 * 있는 인용)를 위한 폴백 용어. 조문 제목·본문·법령명에서 어절을 뽑아
 * 흔한 조사만 떼어 낸다. 원문 문장 자체는 변형하지 않는다.
 */
function fallbackTerms(
  heading: string | undefined,
  statuteText: string | undefined,
  law: string,
): string[] {
  const source = [heading, statuteText, law].filter(Boolean).join(" ");
  const suffixes = ["으로", "에게", "에서", "로", "를", "을", "이", "가", "은", "는", "의", "에", "할", "한"];
  const terms = new Set<string>();

  for (const chunk of source.split(/[^0-9A-Za-z가-힣]+/)) {
    let term = chunk.trim();
    if (term.length < 2) continue;
    terms.add(term);
    for (const suffix of suffixes) {
      if (term.length > suffix.length + 1 && term.endsWith(suffix)) {
        term = term.slice(0, -suffix.length);
        terms.add(term);
        break;
      }
    }
  }

  return [...terms].filter(
    (term) => term.length >= 2 && !FALLBACK_STOPWORDS.has(term),
  );
}

function abortError(): Error {
  if (typeof DOMException === "function") {
    return new DOMException("aborted", "AbortError");
  }
  const error = new Error("aborted");
  error.name = "AbortError";
  return error;
}

/**
 * 리더를 닫았다 다시 열 때의 재조회를 막는 작은 LRU.
 * 근거 추출도 같은 캐시를 쓰지만, 한 검색의 판례 행 수(최대 20)가
 * 이 상한 안에 들어온다.
 */
const CASE_TEXT_CACHE_LIMIT = 20;

/**
 * 조문 → 관련 판례 BM25 질의 문자열.
 * `citation_label + " " + heading + " " + text` (설계 §1). heading이 없으면
 * hierarchy 마지막 조각에서 조문 번호를 뗀 제목을 쓴다.
 */
export function articleRelatedQuery(
  label: string,
  heading: string | undefined,
  hierarchy: string | undefined,
  text: string | undefined,
): string {
  const headingText = heading?.trim() || headingFromHierarchy(hierarchy);
  const query = [label.trim(), headingText, text?.trim()].filter(Boolean).join(" ");
  return query.slice(0, 2048);
}

function headingFromHierarchy(hierarchy?: string): string | undefined {
  if (!hierarchy) return undefined;
  const segments = hierarchy
    .split(/\s*>\s*/)
    .map((segment) => segment.trim())
    .filter(Boolean);
  const last = segments.at(-1);
  if (!last) return undefined;
  const heading = last.replace(/^제\s*\d+조(?:\s*의\s*\d+)?\s*/, "").trim();
  return heading || undefined;
}

export class LocalSearchProvider implements SearchProvider {
  private readonly transport: LocalTransport;
  private readonly options: Required<LocalProviderOptions>;
  private readonly caseTextCache = new Map<string, LocalCaseText>();
  private lastLaw: string | null = null;

  constructor(transport: LocalTransport, options: LocalProviderOptions = {}) {
    this.transport = transport;
    this.options = { ...DEFAULT_OPTIONS, ...options };
  }

  async search(query: string, _epoch: number, signal?: AbortSignal): Promise<Dossier> {
    if (signal?.aborted) throw abortError();
    const route = routeQuery(query);

    if (route.kind === "case") {
      const dossier = await this.caseNumber(query, route.caseNo, route.court, signal);
      if (dossier) return dossier;
      // DB에 없는 사건번호는 자유 텍스트로 되돌린다(사용자는 결과를 본다).
      return this.statutes(query, route.raw, signal);
    }

    if (route.kind === "needs_law") {
      const law = this.lastLaw;
      if (!law) throw new Error(MISSING_LAW_MESSAGE);
      return this.direct(query, law, route.article, signal);
    }
    if (route.kind === "direct") {
      return this.direct(query, route.law, route.article, signal);
    }
    return this.statutes(query, route.text, signal);
  }

  /** 사건번호 1건 → panel "case". 없으면 null(자유 텍스트 폴백). */
  private async caseNumber(
    query: string,
    caseNo: string,
    court: string | undefined,
    signal?: AbortSignal,
  ): Promise<Dossier | null> {
    if (!this.transport.caseByNumber) return null;
    let ref: LocalCaseRef | null = null;
    try {
      ref = await this.transport.caseByNumber(caseNo, court);
    } catch {
      return null;
    }
    if (signal?.aborted) throw abortError();
    if (!ref) return null;

    const textStatus = await this.resolveTextStatus();
    const result = await this.caseResult(ref, 1, "case", [], textStatus);
    return {
      query,
      pairs: [],
      statutes: [],
      cases: [caseCandidate(ref)],
      results: [result],
    };
  }

  private async direct(
    query: string,
    law: string,
    article: string,
    signal?: AbortSignal,
  ): Promise<Dossier> {
    const direct = await this.transport.directCases(law, article);
    if (signal?.aborted) throw abortError();

    if (direct.status !== "ok" || !direct.pointer) {
      // 코어에 없는 법령이면 자유문장 조문 검색으로 되돌린다(사용자는 결과를 본다).
      return this.statutes(query, query, signal);
    }
    this.lastLaw = law;

    const label = direct.label ?? `${law} ${article}`;
    const statute: Candidate = {
      id: direct.pointer,
      kind: "statute",
      label,
      text: direct.text ?? label,
      meta: label,
    };

    const refs = direct.cases;
    const cases = refs.slice(0, this.options.maxCases).map(caseCandidate);
    const textStatus = await this.resolveTextStatus();
    const results = await this.buildDirectResults(
      statute,
      refs,
      law,
      article,
      direct.hierarchy,
      textStatus,
      signal,
    );
    const related = await this.buildRelatedResults(
      direct,
      label,
      results.length,
      textStatus,
      signal,
    );
    results.push(...related);

    const pairs: Pair[] = [];
    for (const [index, ref] of refs.slice(0, this.options.maxPairs).entries()) {
      pairs.push({
        id: `pair-${direct.pointer}-${index}`,
        statute,
        case: caseCandidate(ref),
      });
    }

    return {
      query,
      pairs,
      statutes: [statute],
      cases,
      results,
    };
  }

  private async buildDirectResults(
    statute: Candidate,
    refs: LocalCaseRef[],
    law: string,
    article: string,
    directHierarchy: string | undefined,
    textStatus: CaseTextStatus | undefined,
    signal?: AbortSignal,
  ): Promise<SearchResult[]> {
    const results: SearchResult[] = [
      {
        rank: 1,
        pointer: statute.id,
        label: statute.label,
        hierarchy: directHierarchy,
        citation: statute.label,
        text: statute.text,
        textAvailable: true,
        score: 1,
        panel: "direct",
        evidence: evidence({
          statutes: [statute.label],
          citedCases: refs.slice(0, 3).map(caseLabel),
          panel: "direct",
        }),
      },
    ];

    for (const [index, ref] of refs.slice(0, this.options.maxCases).entries()) {
      if (signal?.aborted) throw abortError();
      const status = await this.caseTextStatus(ref.pointer, textStatus);
      const quote = await this.citationEvidence(
        ref.pointer,
        status,
        law,
        article,
        statute.text,
        directHierarchy,
      );
      results.push({
        rank: results.length + 1,
        pointer: ref.pointer,
        label: caseLabel(ref),
        caseNo: ref.caseNo,
        court: ref.court,
        decisionDate: ref.decisionDate,
        citation: formatCaseCitation(ref),
        text: caseText(ref),
        textAvailable: status === "available",
        score: scoreAt(index + 1) * 0.9,
        caseTextStatus: status,
        panel: "direct",
        provenance: ref.provenance ?? "unknown",
        ...quote,
        evidence: evidence({
          statutes: [statute.label],
          panel: "direct",
          provenance: ref.provenance ?? "body",
        }),
      });
    }

    return results;
  }

  /**
   * 조문 → 관련 판례: **BM25C1** (이웃 조문 휴리스틱 대체).
   *
   * 질의 = citation_label + heading + text. direct 인용 집합은 제외한다.
   * 정렬 = score DESC, decision_date DESC, pointer DESC. `provenance` 없음,
   * `also_cited`는 제외 전에 계산한다(기본 false).
   */
  private async buildRelatedResults(
    direct: LocalDirectCases,
    label: string,
    startRank: number,
    textStatus: CaseTextStatus | undefined,
    signal?: AbortSignal,
  ): Promise<SearchResult[]> {
    if (!this.transport.relatedCases || !direct.locus) return [];

    const query = articleRelatedQuery(label, direct.heading, direct.hierarchy, direct.text);
    if (query.length < 2) return [];

    const exclude = direct.pointers ?? direct.cases.map((ref) => ref.pointer);
    let related: LocalRelatedCases;
    try {
      related = await this.transport.relatedCases(
        direct.locus,
        query,
        exclude,
        this.options.relatedCaseLimit,
      );
    } catch {
      // BM25 볼륨 미설치/열기 실패는 조문 결과를 막지 않는다.
      return [];
    }
    if (signal?.aborted) throw abortError();

    const excluded = new Set(exclude);
    const seen = new Set<string>();
    const hits: LocalCaseHit[] = [];
    for (const hit of related.hits) {
      if (excluded.has(hit.pointer)) continue;
      if (seen.has(hit.pointer)) continue;
      seen.add(hit.pointer);
      hits.push(hit);
    }

    hits.sort((a, b) => {
      if (a.score !== b.score) return b.score - a.score;
      const dateA = a.decisionDate ?? "";
      const dateB = b.decisionDate ?? "";
      if (dateA !== dateB) return dateB.localeCompare(dateA);
      return a.pointer < b.pointer ? 1 : a.pointer > b.pointer ? -1 : 0;
    });

    const results: SearchResult[] = [];
    for (const [index, hit] of hits.slice(0, this.options.relatedCaseLimit).entries()) {
      if (signal?.aborted) throw abortError();
      const result = await this.caseResult(
        {
          pointer: hit.pointer,
          court: hit.court,
          caseNo: hit.caseNo,
          decisionDate: hit.decisionDate,
        },
        startRank + index + 1,
        "related",
        hit.matchedTerms,
        textStatus,
      );
      result.score = hit.score;
      result.alsoCited = hit.alsoCited ?? excluded.has(hit.pointer);
      result.evidence.statutes = [label];
      Object.assign(
        result,
        await this.termEvidence(result.pointer, result.caseTextStatus, hit.matchedTerms),
      );
      results.push(result);
    }
    return results;
  }

  /** BM25 키워드 판례 검색. 결과는 panel "case". */
  private async searchCaseResults(
    query: string,
    startRank: number,
    textStatus: CaseTextStatus | undefined,
    signal?: AbortSignal,
  ): Promise<SearchResult[]> {
    if (!this.transport.searchCases) return [];
    let search: LocalCaseSearch;
    try {
      search = await this.transport.searchCases(query, this.options.caseSearchLimit);
    } catch {
      return [];
    }
    if (signal?.aborted) throw abortError();

    const seen = new Set<string>();
    const results: SearchResult[] = [];
    for (const hit of search.hits) {
      if (seen.has(hit.pointer)) continue;
      seen.add(hit.pointer);
      const result = await this.caseResult(
        {
          pointer: hit.pointer,
          court: hit.court,
          caseNo: hit.caseNo,
          decisionDate: hit.decisionDate,
        },
        startRank + results.length,
        "case",
        hit.matchedTerms,
        textStatus,
      );
      result.score = hit.score;
      result.alsoCited = hit.alsoCited;
      Object.assign(
        result,
        await this.termEvidence(result.pointer, result.caseTextStatus, hit.matchedTerms),
      );
      results.push(result);
    }
    return results;
  }

  private async caseResult(
    ref: LocalCaseRef,
    rank: number,
    panel: "related" | "case",
    matchedTerms: string[],
    textStatus: CaseTextStatus | undefined,
  ): Promise<SearchResult> {
    const status = await this.caseTextStatus(ref.pointer, textStatus);
    return {
      rank,
      pointer: ref.pointer,
      label: caseLabel(ref),
      caseNo: ref.caseNo,
      court: ref.court,
      decisionDate: ref.decisionDate,
      citation: formatCaseCitation(ref),
      text: caseText(ref),
      textAvailable: status === "available",
      score: 0,
      caseTextStatus: status,
      panel,
      evidence: evidence({
        panel,
        ...(matchedTerms.length > 0 ? { matchedTerms } : {}),
      }),
    };
  }

  private async statutes(
    query: string,
    text: string,
    signal?: AbortSignal,
  ): Promise<Dossier> {
    const search = await this.transport.searchStatutes(text, this.options.statuteLimit);
    if (signal?.aborted) throw abortError();

    const hits = search.hits.slice(0, this.options.statuteLimit);
    const textStatus = await this.resolveTextStatus();
    const results: SearchResult[] = hits.map((hit, index) => ({
      rank: index + 1,
      pointer: hit.pointer,
      label: hit.label,
      hierarchy: hit.hierarchy,
      citation: hit.label,
      text: hit.text ?? hit.label,
      textAvailable: hit.text !== undefined,
      score: scoreAt(index + 1),
      panel: "direct",
      evidence: evidence({ statutes: [hit.label], panel: "direct" }),
    }));

    // 자유 텍스트 → 판례 키워드 검색(panel "case"). 조문 결과와 별도 섹션이다.
    const caseResults = await this.searchCaseResults(
      text,
      results.length + 1,
      textStatus,
      signal,
    );
    results.push(...caseResults);

    const cases: Candidate[] = caseResults.map((result) => ({
      id: result.pointer,
      kind: "case",
      label: result.label ?? result.pointer,
      text: result.text,
      meta: [result.court, result.decisionDate].filter(Boolean).join(" ") || undefined,
    }));

    // pairs는 상위 조문의 실제 인용 판례에서 만든다(BM25 결과는 조문 결속이 없다).
    const pairs: Pair[] = [];
    const seenCases = new Set(cases.map((candidate) => candidate.id));
    for (const hit of hits.slice(0, this.options.relatedStatutes)) {
      const parsed = parseStatuteLabel(hit.label);
      if (!parsed) continue;
      const direct = await this.transport.directCases(parsed.law, parsed.article);
      if (signal?.aborted) throw abortError();
      if (direct.status !== "ok") continue;

      for (const [index, ref] of direct.cases.slice(0, this.options.maxPairs).entries()) {
        const statute = results
          .slice(0, hits.length)
          .find((result) => result.pointer === hit.pointer);
        if (statute) {
          pairs.push({
            id: `pair-${hit.pointer}-${index}`,
            statute: {
              id: statute.pointer,
              kind: "statute",
              label: statute.label ?? statute.pointer,
              text: statute.text,
              meta: statute.label,
            },
            case: caseCandidate(ref),
          });
        }
        if (!seenCases.has(ref.pointer)) {
          seenCases.add(ref.pointer);
          cases.push(caseCandidate(ref));
        }
      }
    }

    return { query, pairs, statutes: hits.map(statuteCandidate), cases, results };
  }

  /**
   * 전문 볼륨 준비 상태를 **검색 한 번에 한 번만** 확인한다.
   * 볼륨이 없으면 undefined(=행마다 조회하지 않음)를 돌려준다.
   */
  private async resolveTextStatus(): Promise<CaseTextStatus | undefined> {
    if (!this.transport.caseText) return "missing";
    if (!this.transport.caseTextReady) return undefined;
    try {
      return (await this.transport.caseTextReady()) ? "available" : "missing";
    } catch {
      return "unavailable";
    }
  }

  private async caseTextStatus(
    pointer: string,
    resolved?: CaseTextStatus,
  ): Promise<CaseTextStatus> {
    if (!this.transport.caseText) return "missing";
    if (resolved !== undefined) return resolved;
    try {
      // this.caseText()를 써서 근거 추출과 리더가 같은 LRU를 공유한다.
      const text = await this.caseText(pointer);
      return text.status;
    } catch {
      return "unavailable";
    }
  }

  /** 전문 한 건의 본문. 볼륨이 없거나 status가 available이 아니면 undefined. */
  private async caseTextBody(pointer: string): Promise<string | undefined> {
    if (!this.transport.caseText) return undefined;
    try {
      const document = await this.caseText(pointer);
      return document.status === "available" ? document.text : undefined;
    } catch {
      return undefined;
    }
  }

  /**
   * direct 판례: 조문을 실제로 인용한 문장(최대 3).
   *
   * 참조 목록에만 인용이 있고 판결문 본문에는 "제750조"가 다시 나오지 않는
   * 경우가 많다. 그때는 조문 제목/본문 용어가 나오는 문장을 근거로 쓴다.
   */
  private async citationEvidence(
    pointer: string,
    status: CaseTextStatus,
    law: string,
    article: string,
    statuteText: string,
    hierarchy: string | undefined,
  ): Promise<QuoteFields> {
    if (status !== "available") return {};
    const text = await this.caseTextBody(pointer);
    if (!text) return {};

    const cited = findCitationSentences(text, { lawName: law, article, max: 3 });
    if (cited.length > 0) return quoteFields(cited, "citation");

    return quoteFields(
      findTermSentences(
        text,
        fallbackTerms(headingFromHierarchy(hierarchy), statuteText, law),
        3,
      ),
      "related",
    );
  }

  /** related/keyword 판례: BM25가 맞춘 용어가 나오는 문장(최대 3). */
  private async termEvidence(
    pointer: string,
    status: CaseTextStatus | undefined,
    terms: string[],
  ): Promise<QuoteFields> {
    if (status !== "available" || terms.length === 0) return {};
    const text = await this.caseTextBody(pointer);
    if (!text) return {};
    return quoteFields(findTermSentences(text, terms, 3), "related");
  }

  /** 리더용 on-demand 조회. 최근 20건은 LRU에 남겨 재열기가 즉시 끝난다. */
  async caseText(pointer: string): Promise<LocalCaseText> {
    const cached = this.caseTextCache.get(pointer);
    if (cached) {
      this.caseTextCache.delete(pointer);
      this.caseTextCache.set(pointer, cached);
      return cached;
    }
    if (!this.transport.caseText) return { pointer, status: "missing" };

    let document: LocalCaseText;
    try {
      document = await this.transport.caseText(pointer);
    } catch {
      document = { pointer, status: "unavailable" };
    }
    this.caseTextCache.set(pointer, document);
    while (this.caseTextCache.size > CASE_TEXT_CACHE_LIMIT) {
      const oldest = this.caseTextCache.keys().next().value;
      if (oldest === undefined) break;
      this.caseTextCache.delete(oldest);
    }
    return document;
  }

  async caseTextReady(): Promise<boolean> {
    if (!this.transport.caseTextReady) return false;
    try {
      return await this.transport.caseTextReady();
    } catch {
      return false;
    }
  }
}

/** kernel label("민법 제750조")에서 law/article을 되돌린다. */
export function parseStatuteLabel(label: string): { law: string; article: string } | null {
  const match = /^(.*?)\s*(제\s*\d+조(?:\s*의\s*\d+)?)\s*$/.exec(label.trim());
  if (!match) return null;
  const law = normalizeLawName(match[1]!);
  if (!law) return null;
  const article = match[2]!.replace(/\s+/g, "");
  return { law, article };
}
